import { HTTP_STATUS_CODE, IHttpGet } from '../../adapters/http';
import { IRequestDTO, IResponseDTO } from './dto';
import {
  IGetAssetColorRepo,
  IGetAssetMemory,
  ISetAssetMemory,
  IFundQuotes,
  IAssetQuotes,
  IAssetResponse,
} from '../../interfaces';
import { IChartSeries } from '@mais-retorno-front/models/chart-serie';

export class ComparisonFundQuotesUseCase {
  constructor(
    private readonly url: string,
    private readonly http: IHttpGet<IAssetResponse>,
    private readonly createNetWorthSerie: (
      assetQuotes: IAssetQuotes[]
    ) => IChartSeries[],
    private readonly createQuotaHolderSerie: (
      assetQuotes: IAssetQuotes[]
    ) => IChartSeries[],
    private readonly createDrawDownSerie: (
      assetQuotes: IAssetQuotes[]
    ) => IChartSeries[],
    private readonly createVolatilitySerie: (
      assetQuotes: IAssetQuotes[]
    ) => IChartSeries[],
    private readonly createProfitSerie: (
      assetsQuotes: IAssetQuotes[]
    ) => IChartSeries[],
    private readonly getFundColorRepo: IGetAssetColorRepo,
    private readonly fundMemoryRepo: IGetAssetMemory<IFundQuotes> &
      ISetAssetMemory<IFundQuotes>
  ) {}

  async execute({ fundsIds }: IRequestDTO): Promise<IResponseDTO> {
    const newFundsIds = [...new Set(fundsIds)];

    const fundsQuotes: IAssetQuotes[] = await Promise.all(
      newFundsIds.map(async (id) => {
        const inMemory = this.fundMemoryRepo.get(id);

        if (inMemory) {
          return inMemory;
        }

        const { statusCode, body } = await this.http.get({
          url: `${this.url}/${id}`,
        });

        if (statusCode === HTTP_STATUS_CODE.ok) {
          const newFund = {
            id: id,
            nicename: body.nicename,
            quotes: body.quotes,
          };

          this.fundMemoryRepo.set(id, newFund);

          return newFund;
        }

        return null;
      })
    );

    const fundsQuotesWithoutNullValues = fundsQuotes.filter(Boolean);

    const fundsQuotesWithColors = fundsQuotesWithoutNullValues.map((fund) => ({
      ...fund,
      color: this.getFundColorRepo.getColor(fund.id),
    }));

    const netWorthSeries = this.createNetWorthSerie(fundsQuotesWithColors);
    const quotaHolderSeries = this.createQuotaHolderSerie(
      fundsQuotesWithColors
    );
    const drawDownSeries = this.createDrawDownSerie(fundsQuotesWithColors);
    const volatilitySeries = this.createVolatilitySerie(fundsQuotesWithColors);
    const profitSeries = this.createProfitSerie(fundsQuotesWithColors);

    return {
      fundsQuotes: fundsQuotesWithColors,
      netWorthSeries,
      quotaHolderSeries,
      drawDownSeries,
      volatilitySeries,
      profitSeries,
    };
  }
}
